import type { MatchVerdict } from "@synapse/engine";

import type { GameReport, MoveJudgement } from "./gameReportUtils";

/**
 * One played game as persisted in local history. History is uncapped: it is
 * the single record behind lifetime stats, the history list and whole-history
 * pair dedup, so each entry keeps only what those readers need. The detail
 * view rebuilds a full GameReport with hydrateGameReport.
 */
export interface GameHistoryEntry {
  /** End time; doubles as the entry's key. */
  timestamp: number;
  startWord: string;
  targetWord: string;
  status: GameReport["status"];
  playerPath: string[];
  /**
   * The gold path (persisted key; Ruling R7). Kept, not recomputed: stats
   * compare it to playerPath and bucket by its length (par).
   */
  optimalPath: string[];
  averageSimilarity: number | null;
  /** One digit per recorded move choice; see encodeMarks. */
  marks: string;
  /**
   * Every move made as [from, to, mark], only when a backtrack cut moves from
   * playerPath (otherwise the path and marks already are the full history).
   */
  allMoves?: [string, string, number][];
  /** Only when it differs from the value derived from marks (legacy games). */
  moveAccuracy?: number;
  /** Only when it differs from the value derived from averageSimilarity. */
  playerSemanticDistance?: number;
  backtrackCount?: number;
  achievementIds?: string[];
  isTutorial?: boolean;
  isDailyChallenge?: boolean;
  isChallenge?: boolean;
  dailyChallengeId?: string;
  /** Her thread on any game she played (before her: a daily's reference solution). */
  aiPath?: string[];
  ariadneVerdict?: MatchVerdict;
  ariadneMoves?: number;
}

/**
 * Per move judgement, a digit: 1 Global Optimal + 2 Local Optimal + 4
 * checkpoint (persisted encoding; Ruling R7).
 */
export const encodeMarks = (
  choices: readonly Pick<
    MoveJudgement,
    "isGlobalOptimal" | "isLocalOptimal" | "usedAsCheckpoint"
  >[],
): string =>
  choices
    .map(
      (c) =>
        (c?.isGlobalOptimal ? 1 : 0) +
        (c?.isLocalOptimal ? 2 : 0) +
        (c?.usedAsCheckpoint ? 4 : 0),
    )
    .join("");

export const decodeMark = (
  mark: string,
): {
  isGlobalOptimal: boolean;
  isLocalOptimal: boolean;
  checkpoint: boolean;
} => {
  const n = Number(mark) || 0;
  return {
    isGlobalOptimal: n % 2 === 1,
    isLocalOptimal: Math.floor(n / 2) % 2 === 1,
    checkpoint: Math.floor(n / 4) % 2 === 1,
  };
};

const round = (n: unknown, dp: number): number => {
  const v = typeof n === "number" && Number.isFinite(n) ? n : 0;
  const f = 10 ** dp;
  return Math.round(v * f) / f;
};

/** Slim a finished game's report for history. */
export function toHistoryEntry(report: GameReport): GameHistoryEntry {
  const entry: GameHistoryEntry = {
    timestamp: report.timestamp,
    startWord: report.startWord,
    targetWord: report.targetWord,
    status: report.status,
    playerPath: [...(report.playerPath ?? [report.startWord])],
    optimalPath: [...(report.optimalPath ?? [])],
    averageSimilarity:
      typeof report.averageSimilarity === "number"
        ? round(report.averageSimilarity, 4)
        : null,
    marks: encodeMarks(report.optimalChoices ?? []),
  };
  const accuracy = round(report.moveAccuracy, 2);
  if (Math.abs(accuracy - accuracyOf(entry)) > 0.01) {
    entry.moveAccuracy = accuracy;
  }
  const distance = round(report.playerSemanticDistance, 4);
  if (Math.abs(distance - distanceOf(entry)) > 0.001) {
    entry.playerSemanticDistance = distance;
  }
  const moves = report.moveHistory;
  if (moves && moves.length !== (report.optimalChoices ?? []).length) {
    entry.allMoves = moves.map((m) => [
      m.playerPosition,
      m.playerChose,
      Number(encodeMarks([m])),
    ]);
  }
  const backtracks = report.backtrackEvents?.length ?? report.backtrackCount;
  if (backtracks) entry.backtrackCount = backtracks;
  const ids = (report.earnedAchievements ?? [])
    .map((a) => a?.id)
    .filter((id): id is string => typeof id === "string");
  if (ids.length > 0) entry.achievementIds = ids;
  if (report.isTutorial) entry.isTutorial = true;
  if (report.isDailyChallenge) entry.isDailyChallenge = true;
  if (report.isChallenge) entry.isChallenge = true;
  if (report.dailyChallengeId) entry.dailyChallengeId = report.dailyChallengeId;
  if (
    report.aiPath &&
    report.aiPath.length > 0 &&
    (report.ariadne || report.isDailyChallenge)
  ) {
    entry.aiPath = [...report.aiPath];
  }
  if (report.ariadne) {
    entry.ariadneVerdict = report.ariadne.verdict;
    entry.ariadneMoves = report.ariadne.moves;
  }
  return entry;
}

const isSlimEntry = (x: Record<string, unknown>): boolean =>
  typeof x.marks === "string" && !("optimalChoices" in x);

const words = (v: unknown): string[] | null =>
  Array.isArray(v) ? v.filter((w): w is string => typeof w === "string") : null;

const timestampOf = (rec: Record<string, unknown>): number =>
  Number(rec.timestamp) || Number(rec.id) || 0;

/** The least an entry needs for stats, the history list and pair dedup. */
const minimalEntry = (rec: Record<string, unknown>): GameHistoryEntry => {
  const startWord = rec.startWord as string;
  return {
    timestamp: timestampOf(rec),
    startWord,
    targetWord: rec.targetWord as string,
    status: rec.status === "won" ? "won" : "given_up",
    playerPath: words(rec.playerPath) ?? [startWord],
    optimalPath: [],
    averageSimilarity: null,
    marks: "",
  };
};

const repairSlim = (rec: Record<string, unknown>): GameHistoryEntry => {
  const entry = rec as unknown as GameHistoryEntry;
  if (Array.isArray(rec.playerPath) && Array.isArray(rec.optimalPath)) {
    return entry;
  }
  return {
    ...entry,
    playerPath: words(rec.playerPath) ?? [entry.startWord],
    optimalPath: words(rec.optimalPath) ?? [],
  };
};

const fromLegacy = (rec: Record<string, unknown>): GameHistoryEntry => {
  const legacy = rec as unknown as GameReport;
  const list = <T>(v: T[] | undefined): T[] => (Array.isArray(v) ? v : []);
  return toHistoryEntry({
    ...legacy,
    timestamp: timestampOf(rec),
    status: legacy.status === "won" ? "won" : "given_up",
    playerPath: words(legacy.playerPath) ?? [legacy.startWord],
    optimalPath: words(legacy.optimalPath) ?? [],
    optimalChoices: list(legacy.optimalChoices),
    earnedAchievements: list(legacy.earnedAchievements),
    backtrackEvents: Array.isArray(legacy.backtrackEvents)
      ? legacy.backtrackEvents
      : undefined,
    aiPath: words(legacy.aiPath) ?? undefined,
  });
};

/**
 * Convert stored history (legacy full GameReports, slim entries, or a mix) to
 * slim entries. Idempotent and total: runs on load, so a malformed entry must
 * never throw. One that cannot be slimmed keeps a minimal entry; entries
 * without a start and target word are dropped, since no reader can use them.
 */
export function migrateHistory(stored: unknown): GameHistoryEntry[] {
  if (!Array.isArray(stored)) return [];
  const out: GameHistoryEntry[] = [];
  for (const item of stored) {
    if (!item || typeof item !== "object") continue;
    const rec = item as Record<string, unknown>;
    if (typeof rec.startWord !== "string" || typeof rec.targetWord !== "string")
      continue;
    try {
      out.push(isSlimEntry(rec) ? repairSlim(rec) : fromLegacy(rec));
    } catch (error) {
      console.warn("Kept a minimal history entry for a malformed game", error);
      try {
        out.push(minimalEntry(rec));
      } catch {
        // Unreadable even at the top level: drop only this entry.
      }
    }
  }
  return out;
}

/** Moves made in a history entry. */
export const movesOf = (entry: Pick<GameHistoryEntry, "playerPath">): number =>
  Math.max(0, entry.playerPath.length - 1);

/**
 * Move accuracy as generateGameReport computes it: the share of moves that
 * were Local Optimal (which includes Global Optimal), capped at 100.
 */
export function accuracyOf(
  entry: Pick<GameHistoryEntry, "playerPath" | "marks" | "moveAccuracy">,
): number {
  if (typeof entry.moveAccuracy === "number") return entry.moveAccuracy;
  const moves = movesOf(entry);
  if (moves === 0) return 0;
  let localOptimal = 0;
  for (const m of entry.marks) {
    const d = decodeMark(m);
    if (d.isGlobalOptimal || d.isLocalOptimal) localOptimal++;
  }
  return round(Math.min(100, (localOptimal / moves) * 100), 2);
}

/**
 * Semantic distance travelled. Each move costs 1 - similarity (1 for a missing
 * edge, which averageSimilarity counts as 0), so the total is
 * moves * (1 - averageSimilarity).
 */
export function distanceOf(
  entry: Pick<
    GameHistoryEntry,
    "playerPath" | "averageSimilarity" | "playerSemanticDistance"
  >,
): number {
  if (typeof entry.playerSemanticDistance === "number") {
    return entry.playerSemanticDistance;
  }
  if (typeof entry.averageSimilarity !== "number") return 0;
  return round(movesOf(entry) * (1 - entry.averageSimilarity), 4);
}
